import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { isRebrandingEnabled } from "@src/appV2/redesign/system/ui-theme";
import { type LocationState } from "history";

import { Button } from "../../components/Button";
import { LoadingButton } from "../../components/LoadingButton";
import { WorkplaceModalFloatingFooter } from "../Modal/FloatingFooter";
import { ViewStaffJobsButton } from "../OpenShifts/ViewStaffJobsButton";

interface WorkplaceProfileModalFooterProps {
  workplaceId: string;
  isLoading: boolean;
  locationState: LocationState;
  source: "flex-workplace-profile-modal" | "hiring-workplace-profile-modal";
  /**
   * Omit to hide the primary open-shifts CTA entirely (e.g. Home Healthcare/Hospice
   * workplaces with no open shifts).
   */
  openShifts?: {
    count: number;
    href: string;
    analyticsTrigger: string;
  };
  /**
   * Omit to hide the secondary staff-jobs CTA (feature disabled or no placements).
   */
  staffJobs?: {
    placementsCount: number;
    placementCandidateId: string | undefined;
  };
}

export function WorkplaceProfileModalFooter(props: WorkplaceProfileModalFooterProps) {
  const { isLoading, ...contentProps } = props;

  return (
    <WorkplaceModalFloatingFooter>
      <Stack spacing={3} sx={{ width: "100%" }}>
        {isLoading ? (
          <LoadingButton
            fullWidth
            isLoading
            variant="contained"
            sx={(theme) => ({
              // !important is needed to override the default `box-shadow: none` on the button
              boxShadow: `${theme.shadows[3]} !important`,
            })}
          >
            Loading
          </LoadingButton>
        ) : (
          <FooterContent {...contentProps} />
        )}
      </Stack>
    </WorkplaceModalFloatingFooter>
  );
}

type FooterContentProps = Omit<WorkplaceProfileModalFooterProps, "isLoading">;

function FooterContent(props: FooterContentProps) {
  const { workplaceId, locationState, source, openShifts, staffJobs } = props;

  const isHiring = source === "hiring-workplace-profile-modal";
  const showOpenShiftsButton = isDefined(openShifts) && !(isHiring && openShifts.count === 0);

  if (isHiring) {
    return (
      <>
        {staffJobs && (
          <ViewStaffJobsButton
            variant="contained"
            workplaceId={workplaceId}
            source={source}
            placementsCount={staffJobs.placementsCount}
            placementCandidateId={staffJobs.placementCandidateId}
            locationState={locationState}
          />
        )}
        {showOpenShiftsButton && (
          <OpenShiftsButton
            variant="outlined"
            workplaceId={workplaceId}
            locationState={locationState}
            {...openShifts}
          />
        )}
      </>
    );
  }

  return (
    <>
      {openShifts && (
        <OpenShiftsButton
          variant="contained"
          workplaceId={workplaceId}
          locationState={locationState}
          {...openShifts}
        />
      )}
      {staffJobs && (
        <ViewStaffJobsButton
          variant="outlined"
          workplaceId={workplaceId}
          source={source}
          placementsCount={staffJobs.placementsCount}
          placementCandidateId={staffJobs.placementCandidateId}
          locationState={locationState}
        />
      )}
    </>
  );
}

interface OpenShiftsButtonProps {
  workplaceId: string;
  count: number;
  href: string;
  analyticsTrigger: string;
  locationState: LocationState;
  variant: "contained" | "outlined";
}

function OpenShiftsButton(props: OpenShiftsButtonProps) {
  const { workplaceId, count, href, analyticsTrigger, locationState, variant } = props;
  const hasOpenShifts = count > 0;

  return (
    <Button
      fullWidth
      variant={variant}
      size={isRebrandingEnabled() && variant === "contained" && hasOpenShifts ? "large" : undefined}
      invert={variant === "contained" && !hasOpenShifts}
      disabled={!hasOpenShifts}
      href={href}
      locationState={locationState}
      sx={(theme) => ({
        // !important is needed to override the default `box-shadow: none` on the button
        boxShadow: `${theme.shadows[3]} !important`,
      })}
      onClick={() => {
        logEvent(APP_V2_USER_EVENTS.WORKPLACE_OPEN_SHIFTS_OPENED, {
          workplaceId,
          trigger: analyticsTrigger,
        });
      }}
    >
      {hasOpenShifts ? `View ${count} open shifts` : "No open shifts right now"}
    </Button>
  );
}
